import { DASHBOARD_PATH, SIGN_IN_PATH } from '@/constants/pages';
import { identifyUser } from '@/lib/analytics';
import { fraudFrontQueryClient } from '@/lib/react-query';
import { components } from '@/types/ff.v1';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

type UseSessionOptions = {
  redirectTo?: string;
  redirectIfFound?: boolean;
  skipRedirect?: boolean;
};

export const useSession = (
  options: UseSessionOptions = {}
): {
  isLoading: boolean;
  session: components['schemas']['CustomerView'] | undefined;
} => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const {
    redirectTo = SIGN_IN_PATH,
    redirectIfFound = false,
    skipRedirect = false
  } = options;
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Since we can't access HttpOnly cookies, we make an API call
  // that will check for the cookie server-side
  const { data, status } = fraudFrontQueryClient.useQuery(
    'get',
    '/api/v1/customers/profile'
  );

  // Handle redirects based on session status
  useEffect(() => {
    if (skipRedirect || isRedirecting) {
      return;
    }

    // If the API call succeeded, we have a valid session
    if (status === 'success' && data) {
      if (redirectIfFound) {
        // If we're on a page that requires no auth but user is authenticated
        setIsRedirecting(true);
        router.replace(DASHBOARD_PATH);
      }
    } else if (status === 'error' && !redirectIfFound) {
      // If the API call failed (no valid session) and we need auth
      setIsRedirecting(true);
      const currentPath = router.asPath;
      const query = { returnTo: encodeURIComponent(currentPath) };
      router.replace({
        pathname: redirectTo,
        query
      });
    }
  }, [
    status,
    data,
    isRedirecting,
    redirectIfFound,
    redirectTo,
    router,
    skipRedirect
  ]);

  // Identify the customer to PostHog at the single authenticated chokepoint
  // every protected/promo page hits. Runs regardless of redirect options so it
  // also covers skipRedirect pages. identifyUser short-circuits when already
  // identified as this user, and merges the prior anonymous distinct_id —
  // retroactively linking pre-signup funnel events to the converted person.
  useEffect(() => {
    if (status !== 'success' || data?.customer?.id == null) {
      return;
    }
    const { id, email, firstName } = data.customer;
    identifyUser(String(id), {
      email,
      first_name: firstName
    });
  }, [status, data]);

  // Update loading state
  useEffect(() => {
    if ((status === 'success' || status === 'error') && !isRedirecting) {
      setIsLoading(false);
    }
  }, [status, isRedirecting]);

  const customerData = data;

  return {
    isLoading: isLoading || isRedirecting,
    session: customerData?.customer
  };
};
